import React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  PgHero, PgHeroInner, PgTitle, PgIn, PgPill, PgSurface,
  PgSplit, PgSplitText, PgSplitPanel, PgFeats, PgMini, PgBreak,
  PgOutro, PgOutroItem,
} from './product';

const BREAK_VIDEO = 'https://cloudflare.pactilink.com/website/Head_Movement_and_Feathers.mp4';
const APP_URL = 'https://app.ortexo.com';
const SITE_URL = 'https://ortexo.com';

export const OrtexoPage = () => {
  return (
    <>
      {/* Hero */}
      <PgHero>
        <PgHeroInner>
          <PgTitle
            lines={[
              'Găsești legea și',
              <span className="accent">răspunsul, cu articolul citat.</span>,
            ]}
          />
          <PgIn delay={0.65}>
            <p className="pg-lead">
              Întrebi în limbaj obișnuit. Ortexo îți dă răspunsul cu trimiterea exactă la lege —
              pe care o poți deschide și cita direct. Legislație, Cod fiscal, TVA, norme contabile
              și jurisprudență, toate la zi.
            </p>
          </PgIn>
          <PgIn delay={0.78} className="pg-cta">
            <PgPill href={APP_URL} target="_blank" rel="noopener noreferrer">
              Deschide Ortexo <ArrowRight size={17} />
            </PgPill>
            <a href="#detalii" className="pg-ghost">Vezi cum arată</a>
          </PgIn>
        </PgHeroInner>

        {/* Q&A surface — real content, no chrome */}
        <PgSurface id="detalii">
          <div className="pg-surface-head">
            <span className="pg-surface-title">Întrebare</span>
            <span className="pg-surface-meta">cercetare juridică</span>
          </div>
          <motion.div
            style={{ padding: 'clamp(1.5rem, 4vw, 2.75rem)', display: 'flex', flexDirection: 'column', gap: '1.4rem' }}
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.16, delayChildren: 1.15 } } }}
          >
            <motion.p
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.15rem, 2vw, 1.5rem)', color: 'var(--text-primary)', lineHeight: 1.4, letterSpacing: '-0.01em' }}
            >
              „Care este termenul de contestare a unui plan de reorganizare?”
            </motion.p>

            <motion.div
              variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ borderLeft: '2px solid var(--copper-dim)', paddingLeft: '1.25rem' }}
            >
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.98rem' }}>
                Conform Legii nr. 85/2014, planul de reorganizare poate fi contestat în termen de{' '}
                <strong style={{ color: 'var(--copper-light)', fontWeight: 600 }}>30 de zile</strong> de la publicarea în BPI.
              </p>
              <motion.div
                style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem', flexWrap: 'wrap' }}
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } } }}
              >
                {['Legea 85/2014, Art. 139', 'Art. 141 alin. (2)'].map((s) => (
                  <motion.span
                    key={s}
                    variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}
                    transition={{ type: 'spring', stiffness: 200, damping: 16 }}
                    style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.28rem 0.6rem' }}
                  >
                    {s}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        </PgSurface>
      </PgHero>

      {/* Split 1 — coverage breadth */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Nu doar un domeniu — <span className="accent">tot dreptul, fiscalul și contabilitatea.</span></h2>
              <p className="pg-split-p">
                Ortexo reunește legislația, Codul fiscal și TVA, normele contabile OMFP și jurisprudența
                într-un singur loc. Jurisprudența include instanțele de fond și Înalta Curte — inclusiv
                deciziile de RIL și hotărârile prealabile.
              </p>
              <PgFeats items={[
                'Legislație, Cod fiscal și TVA',
                'Norme contabile (OMFP)',
                'Jurisprudență, inclusiv ÎCCJ (RIL / HP)',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini
                label="Ce acoperă"
                rows={[
                  { k: 'Legislație', v: 'coduri, legi, OUG, HG' },
                  { k: 'Fiscal', v: 'Cod fiscal, TVA' },
                  { k: 'Contabil', v: 'norme OMFP' },
                  { k: 'Jurisprudență', v: 'inclusiv ÎCCJ', hi: true },
                ]}
              />
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* Cinematic break — video only, no text */}
      <PgBreak src={BREAK_VIDEO} />

      {/* Split 2 — in force + point in time */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Legea de azi — <span className="accent">sau așa cum era la o dată anume.</span></h2>
              <p className="pg-split-p">
                În mod normal lucrezi cu forma aflată în vigoare. Dar când ai nevoie de trecut — „ce cotă
                de TVA era în iunie 2025?” — Ortexo îți dă articolul exact așa cum arăta la acea dată.
                Poți ajunge chiar și la texte abrogate, când un litigiu le cere.
              </p>
              <PgFeats items={[
                'În vigoare, implicit și verificat',
                'Versiunea legii la o dată din trecut',
                'Text abrogat, la cerere, marcat clar',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini
                label="Versiune act"
                rows={[
                  { k: 'La data', v: 'iunie 2025', hi: true },
                  { k: 'Cotă TVA', v: 'forma de atunci' },
                  { k: 'Stare azi', v: 'modificat' },
                ]}
              />
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* Split 3 — decisions + cross references */}
      <section className="section">
        <div className="container">
          <PgSplit reverse>
            <PgSplitPanel>
              <PgMini
                label="Art. 139 — legături"
                rows={[
                  { k: 'Modificat de', v: '2 acte' },
                  { k: 'Invocat în', v: 'art. 141, 145' },
                  { k: 'Decizii', v: 'interpretat de 4', hi: true },
                ]}
              />
            </PgSplitPanel>
            <PgSplitText>
              <h2 className="pg-split-h">Decizia întreagă, cu citarea ei. <span className="accent">Și tot ce se leagă de un articol.</span></h2>
              <p className="pg-split-p">
                Scoți textul integral al unei decizii — dispozitivul sau considerentele — cu citarea gata
                de pus în act. Iar pentru orice articol vezi harta lui: ce l-a modificat, cine îl invocă
                și care sunt hotărârile care îl interpretează — cu semnal dacă mai e lege bună.
              </p>
              <PgFeats items={[
                'Text integral al deciziei, cu citare corectă',
                'Ce a modificat un articol și cine îl invocă',
                'Deciziile care îl interpretează',
              ]} />
            </PgSplitText>
          </PgSplit>
        </div>
      </section>

      {/* Split 4 — MCP / AI assistant */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Ortexo, direct <span className="accent">în asistentul tău AI.</span></h2>
              <p className="pg-split-p">
                Conectezi Ortexo la asistentul pe care îl folosești deja — Claude sau ChatGPT — și pui
                întrebări de acolo. Răspund din baza Ortexo, cu articolul și decizia citate, nu din
                memorie și nu de pe web. Conexiunea e securizată și doar-citire.
              </p>
              <PgFeats items={[
                'Se conectează la Claude și ChatGPT',
                'Răspunsuri citate din baza Ortexo',
                'Acces securizat, doar-citire',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini
                label="Conexiune"
                rows={[
                  { k: 'Client', v: 'Claude · ChatGPT' },
                  { k: 'Acces', v: 'doar-citire' },
                  { k: 'Sursă', v: 'baza Ortexo, citat', hi: true },
                ]}
              />
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <PgOutro>
            <PgOutroItem as="h2" className="pg-h1" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', marginBottom: '1rem' }}>
              Ortexo este <span className="accent">disponibil acum.</span>
            </PgOutroItem>
            <PgOutroItem as="p" className="pg-lead" style={{ margin: '0 auto 2.25rem' }}>
              Intră în aplicație și pune prima întrebare, sau află mai multe pe ortexo.com.
            </PgOutroItem>
            <PgOutroItem className="pg-cta" style={{ justifyContent: 'center' }}>
              <PgPill href={APP_URL} target="_blank" rel="noopener noreferrer">
                Deschide aplicația <ArrowRight size={17} />
              </PgPill>
              <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="pg-ghost">Vezi ortexo.com</a>
            </PgOutroItem>
          </PgOutro>
        </div>
      </section>
    </>
  );
};

export default OrtexoPage;
